export type PerformanceOpt = {
  debug?: boolean;
  loadReport?: boolean;
  flag?: string;
  performanceInfo?: {
    _hmt?: string[];
    whiteScreenTime?: number;
    firstScreenTime?: number;
    pageTimeout?: number;
    apiTimeout?: number;
    pageview?: string;
    subPageview?: string;
  };
};

export function setupPerformance(win: any, dom: Document, opt: PerformanceOpt) {
  opt = opt || {};
  const flag = opt.flag || '';
  const TAG = `[performance][${flag}]`;
  const debug = opt.debug || win.location.search.indexOf('remotelog=1') > 0;

  // 避免重复处理
  if (win.performanceInfo) {
    debug && console.warn(TAG, 'inited');
    return;
  }

  // 获取小程序配置
  function getExtInfo() {
    const data = {
      extName: '',
      extType: '',
      extUuid: '',
      extVersion: '',
      extBuildEnv: '',
      diyPerformanceReport: '', // 开启自定义上报 <meta name="diy-performance-report" content="open">
    };
    Object.keys(data).forEach((key) => {
      const meta = key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
      const ele = dom.querySelector(`meta[name="${meta}"]`);
      data[key as keyof typeof data] = ele?.getAttribute('content') as string;
    });
    return data;
  }

  const ext = getExtInfo();
  // 初始化配置
  win.performanceInfo = {
    _hmt: [],
    whiteScreenTime: Date.now(),
    firstScreenTime: 0,
    // 小程序react首次render时间
    firstRenderTime: 0,
    pageTimeOut: 5000,
    apiTimeOut: 2000,
    pageview: win.location.href,
    subPageview: '', // 标记来源
    __fstScreenType: '', // 首屏时间类型
    setFirstRenderTime: (time?: number) => {
      if (!(win.performance && typeof (win.performance.timing) == "object" && win.performance.timing.fetchStart > 0)) {
        return;
      }
      if (!win.performanceInfo.firstRenderTime) {
        win.performanceInfo.firstRenderTime = time || Date.now();
        win.performanceInfo._hmt.push(["reportTime", {
          dim: {
            path: 'extfirstrendered',
            extname: ext.extName,
            exttype: ext.extType,
            extuuid: ext.extUuid,
            extversion: ext.extVersion,
            extbuildenv: ext.extBuildEnv,
            extdesc: JSON.stringify({
              firstRenderTime: win.performanceInfo.firstRenderTime,
              fetchStart: win.performance.timing.fetchStart,
            }),
          },
          field: {
            value: win.performanceInfo.firstRenderTime - win.performance.timing.fetchStart
          }
        }])
      }
    },
  };

  function setFirstScreenTime(type: string, time?: number) {
    if (!win.performanceInfo.firstScreenTime) {
      win.performanceInfo.firstScreenTime = time || Date.now();
      win.performanceInfo.__fstScreenType = type;
    }
  }

  // 获取 pageview 即报表里的 version
  function getPageView(ext: any) {
    const { extUuid, extType, extBuildEnv } = ext || {};
    // ext信息获取失败会用以前的 version 规则上报
    if (!extUuid && !extType) {
      return `${win.location.origin}${win.location.pathname || ''}`;
    }
    const env_ = extBuildEnv === 'production' ? '' : `${extBuildEnv || 'dev'}_`;
    return `hyext_${env_}${extUuid || ''}/${extType || ''}`;
  }

  // 加载 report
  function loadScript() {
    const params = ''; // `?version=${}` Math.floor(new Date().setMinutes(0) / 100000); // 每小时变化一次
    const src = `//a.msstatic.com/huya/hd/cdn_libs/performance_report-min.js${params}`;
    const scripts = dom.getElementsByTagName('script');
    // 避免重复加载
    if (Array.from(scripts).some((s) => s.src.indexOf(src) > -1)) return;
    const fst = scripts[0];
    const report = dom.createElement('script');
    report.src = src;
    fst?.parentNode?.insertBefore(report, fst);
    debug && console.log(TAG, 'load report');
  }

  // 初始化
  try {
    // 设置基础信息
    Object.assign(
      win.performanceInfo,
      {
        pageview: getPageView(ext),
        subPageview: `${ext.extVersion || ''}${flag ? `_${flag}` : ''}`, // 标记来源
        udbUid: (dom.cookie?.match(/udb_uid=([^;]+)/g) || [])[0]?.split('=')[1],
      },
      opt.performanceInfo || {},
    );

    // 构建环境判断 production, test
    if (ext.extBuildEnv === 'dev') {
      debug && console.warn(TAG, 'stop build-env:', ext);
      return;
    }

    if (opt.loadReport) {
      // 加载 script
      loadScript();
    }

    // 自定义性能上报, 开启后业务可通过赋值 win.performanceInfo.firstScreenTime 上报首屏时间
    if (ext.diyPerformanceReport) {
      debug && console.warn(TAG, 'use diy report', ext);
      return;
    }

    // 监听 hyExt 赋值上报 firstScreenTime
    let value = win.hyExt;
    if (!value || !value.getConfig) {
      Object.defineProperty(win, 'hyExt', {
        get: function () {
          return value;
        },
        set: function (newValue) {
          value = newValue;
          setFirstScreenTime('sdkinit');
        },
      });
    } else {
      // @ts-ignore hyExt 已存在
      const { inited } = hyExt.getConfig();
      if (inited) {
        win.performanceInfo.sdkInitTime = inited;
        setFirstScreenTime('inited', inited);
      }
    }

    // // 监听 dom 变化，赋值 firstScreenTime
    // dom.addEventListener('DOMContentLoaded', function () {
    //   const root =
    //     dom.getElementById('root') || dom.getElementsByTagName('div')[0];

    //   try {
    //     new MutationObserver(function (mutationList, observer) {
    //       // 触发即销毁监听
    //       observer.disconnect();
    //       // 只在 sdkInitTime 未赋值时触发有效
    //       setFirstScreenTime('observer');
    //     }).observe(root, {
    //       attributes: true,
    //       childList: true,
    //       subtree: true,
    //       characterDataOldValue: true, // 文本变更
    //     });
    //   } catch (err) {
    //     debug && console.warn(TAG, 'observer fail', err);
    //   }
    // });
  } catch (err) {
    debug && console.warn(TAG, 'setup fail', err);
  }
}
